// Diagnostic Logger - Sends logs to backend for viewing via SSH
import axios from 'axios';

// Use a simple endpoint that writes to frontend server file
// This will be handled by a simple Python script running on the frontend server
const LOG_ENDPOINT = '/api/diagnostic-logs/';

// Queue logs to batch send (reduces API calls)
let logQueue = [];
let sendTimeout = null;
const BATCH_DELAY = 2000; // Send logs every 2 seconds
const MAX_QUEUE_SIZE = 50; // Max logs to queue before forcing send

// Get user agent and device info
const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    standalone: window.matchMedia('(display-mode: standalone)').matches || 
                window.navigator.standalone || 
                document.referrer.includes('android-app://'),
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    url: window.location.href,
    pathname: window.location.pathname,
    hash: window.location.hash,
    search: window.location.search
  };
};

// Get service worker info
const getServiceWorkerInfo = async () => {
  if (!('serviceWorker' in navigator)) {
    return { supported: false };
  }
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return { registered: false };
    }
    
    return {
      registered: true,
      scope: registration.scope,
      active: registration.active ? {
        state: registration.active.state,
        scriptURL: registration.active.scriptURL
      } : null,
      installing: registration.installing ? {
        state: registration.installing.state
      } : null,
      waiting: registration.waiting ? {
        state: registration.waiting.state
      } : null,
      controller: navigator.serviceWorker.controller ? {
        state: navigator.serviceWorker.controller.state,
        scriptURL: navigator.serviceWorker.controller.scriptURL
      } : null
    };
  } catch (error) {
    return { error: error.message };
  }
};

// Send queued logs to backend
const sendLogs = async (useBeacon = false) => {
  if (logQueue.length === 0) return;
  
  const logsToSend = [...logQueue];
  logQueue = [];
  
  try {
    const deviceInfo = getDeviceInfo();
    const swInfo = await getServiceWorkerInfo();
    
    const payload = {
      timestamp: new Date().toISOString(),
      device_info: deviceInfo,
      service_worker_info: swInfo,
      logs: logsToSend
    };
    
    // Use sendBeacon if available and during page unload (more reliable)
    if (useBeacon && navigator.sendBeacon) {
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const sent = navigator.sendBeacon(LOG_ENDPOINT, blob);
        if (sent) {
          return; // sendBeacon succeeded
        }
        // If sendBeacon returns false, fall through to regular fetch
      } catch (beaconError) {
        // Fall through to regular fetch
      }
    }
    
    // Send to backend (don't wait for response to avoid blocking)
    axios.post(LOG_ENDPOINT, payload, {
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json'
      }
    }).catch(error => {
      // In development, the log server isn't running - 404 is expected
      // Only show warnings for non-404 errors, or in production
      const isDevelopment = process.env.NODE_ENV === 'development' || 
                           window.location.hostname === 'localhost' ||
                           window.location.hostname === '127.0.0.1';
      
      if (isDevelopment && error.response?.status === 404) {
        // 404 is expected in development - log server not running
        // Silently ignore
        return;
      }
      
      // For other errors or in production, log a warning (but don't spam)
      if (error.response?.status !== 404) {
        console.warn('Failed to send diagnostic logs:', error.message);
      }
    });
  } catch (error) {
    // Only warn in production - in dev, log server isn't running
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';
    
    if (!isDevelopment) {
      console.warn('Error preparing diagnostic logs:', error.message);
    }
  }
};

// Schedule log sending
const scheduleSend = () => {
  if (sendTimeout) {
    clearTimeout(sendTimeout);
  }
  
  sendTimeout = setTimeout(() => {
    sendLogs();
    sendTimeout = null;
  }, BATCH_DELAY);
};

// Main logging function
export const logDiagnostic = (category, event, data = {}, immediate = false) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    category, // e.g., 'navigation', 'service-worker', 'component', 'error'
    event,    // e.g., 'navigate-to-jobs', 'sw-message-received', 'component-mount'
    data,
    stack: new Error().stack // Capture stack trace for debugging
  };
  
  // Also log to console for immediate debugging
  console.log(`[DIAGNOSTIC] ${category}: ${event}`, data);
  
  // Add to queue
  logQueue.push(logEntry);
  
  // If immediate flag is set or queue is too large, send right away
  if (immediate || logQueue.length >= MAX_QUEUE_SIZE) {
    sendLogs();
  } else {
    scheduleSend();
  }
};

// Flush logs immediately (useful before page unload)
export const flushLogs = (useBeacon = false) => {
  if (sendTimeout) {
    clearTimeout(sendTimeout);
    sendTimeout = null;
  }
  sendLogs(useBeacon);
};

// Log navigation events
export const logNavigation = (from, to, method = 'unknown') => {
  logDiagnostic('navigation', 'route-change', {
    from,
    to,
    method, // 'navigate', 'popstate', 'link-click', etc.
    timestamp: Date.now()
  });
};

// Log service worker events
export const logServiceWorker = (event, data = {}) => {
  logDiagnostic('service-worker', event, data);
};

// Log component lifecycle
export const logComponent = (component, event, data = {}) => {
  logDiagnostic('component', `${component}-${event}`, data);
};

// Log errors
export const logError = (error, context = {}) => {
  logDiagnostic('error', 'error-occurred', {
    message: error.message,
    stack: error.stack,
    name: error.name,
    ...context
  });
};

// Flush logs on page unload
if (typeof window !== 'undefined') {
  // Make flushLogs available globally for beforeunload handler
  window.flushDiagnosticLogs = () => flushLogs(true); // Use beacon for unload
  
  window.addEventListener('beforeunload', () => {
    // Send logs using sendBeacon (works during page unload)
    if (logQueue.length > 0) {
      flushLogs(true); // Force use of sendBeacon
    }
  });
  
  window.addEventListener('pagehide', () => {
    // Also try on pagehide (more reliable than beforeunload in some browsers)
    if (logQueue.length > 0) {
      flushLogs(true); // Force use of sendBeacon
    }
  });
  
  window.addEventListener('pagehide', () => {
    if (logQueue.length > 0) {
      flushLogs();
    }
  });
  
  // Also flush periodically (every 5 seconds) to ensure logs aren't lost
  setInterval(() => {
    if (logQueue.length > 0) {
      sendLogs();
    }
  }, 5000);
  
  // Log when page is about to reload (if we can detect it)
  let lastUrl = window.location.href;
  setInterval(() => {
    if (window.location.href !== lastUrl) {
      logDiagnostic('page', 'url-changed', {
        from: lastUrl,
        to: window.location.href,
        isReload: window.performance?.navigation?.type === 1
      });
      lastUrl = window.location.href;
    }
  }, 100);
}
